import * as ScrollArea from '@radix-ui/react-scroll-area';
import React, { Children, type ReactNode, useRef } from 'react';
import { Virtualizer, type VirtualizerHandle } from 'virtua';

import { cn } from '~/utils/cn.ts';

export function VirtualizedViewport({
	children,
	className,
	selectedIndex,
	setSelectedIndex,
	style: _style,
	ref,
}: {
	children: ReactNode;
	className?: string;
	selectedIndex: number;
	setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
	style?: React.CSSProperties;
	ref?: React.RefObject<HTMLDivElement | null>;
}) {
	const count = Children.count(children);
	const virtualizerRef = useRef<VirtualizerHandle>(null);
	return (
		<ScrollArea.Viewport
			className={cn(className, '[&>div]:block [&>div]:h-full')}
			onKeyDown={(e) => {
				if (!virtualizerRef.current) {
					return;
				}
				switch (e.code) {
					case 'ArrowUp': {
						e.preventDefault();
						const prevIndex = Math.max(selectedIndex - 1, 0);
						setSelectedIndex(prevIndex);
						virtualizerRef.current.scrollToIndex(prevIndex, {
							align: 'nearest',
						});
						break;
					}
					case 'ArrowDown': {
						e.preventDefault();
						const nextIndex = Math.min(selectedIndex + 1, count - 1);
						setSelectedIndex(nextIndex);
						virtualizerRef.current.scrollToIndex(nextIndex, {
							align: 'nearest',
						});
						break;
					}
				}
			}}
			ref={ref}
			tabIndex={0}
		>
			<Virtualizer ref={virtualizerRef} scrollRef={ref}>
				{children}
			</Virtualizer>
		</ScrollArea.Viewport>
	);
}

export function VirtualizedScrollArea({
	classNames,
	children,
	styles,
	type,
	orientation,
	virtualizedProps,
}: React.PropsWithChildren<{
	classNames: {
		root?: string;
		viewport?: string;
		scrollbar?: string;
		thumb?: string;
		corner?: string;
	};
	styles?: {
		root?: React.CSSProperties;
	};
	type: 'auto' | 'always' | 'scroll' | 'hover' | undefined;
	orientation: 'horizontal' | 'vertical' | undefined;
	virtualizedProps: {
		selectedIndex: number;
		setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
	};
}>) {
	const viewportRef = useRef<HTMLDivElement>(null);

	return (
		<ScrollArea.Root
			className={cn('relative overflow-hidden', classNames.root)}
			data-slot="virtualized-scroll-area"
			style={styles?.root}
			type={type}
		>
			<VirtualizedViewport
				className={classNames.viewport}
				ref={viewportRef}
				selectedIndex={virtualizedProps.selectedIndex}
				setSelectedIndex={virtualizedProps.setSelectedIndex}
			>
				{children}
			</VirtualizedViewport>
			<ScrollArea.Scrollbar
				className={cn(
					'z-30 flex touch-none select-none p-[2px] transition-colors',
					orientation === 'vertical' ? 'w-2.5' : 'h-2.5 flex-col',
					classNames.scrollbar,
				)}
				data-slot="virtualized-scrollbar"
				orientation={orientation}
			>
				<ScrollArea.Thumb
					className={cn('relative flex-1 rounded-none bg-border', classNames.thumb)}
					data-slot="virtualized-thumb"
				/>
			</ScrollArea.Scrollbar>
			<ScrollArea.Corner className={cn('bg-muted', classNames.corner)} data-slot="virtualized-corner" />
		</ScrollArea.Root>
	);
}
